// TEMP until after the 22nd of April.

import { useRef, useEffect } from 'react';

import { type Moment, moment } from '@hotjar/insights-common/lib/moment';
import { getAbsoluteDateRange, HEATMAP_DATA_TYPES } from '@hotjar/heatmaps';
import { DATE_RANGE_TYPE, type DateRange } from '@hotjar/global-filters';
import { showContent as showAppcuesContent } from '@hotjar/webapp/tracking/tools/appcues';

const unavailableStartDate = moment('2022-03-13').startOf('day');
const unavailableEndDate = moment('2022-04-22').endOf('day');

const WITHIN_RANGE_FLOW_ID = 'e7e16737-36d6-4399-82be-4e51163be36e';
const OVERLAPS_RANGE_FLOW_ID = 'b4a3c2a1-25aa-4eb9-ab47-17c784227829';

function getStartAndEndDates(
  dateRange: DateRange,
): { startDate: Moment; endDate: Moment } | undefined {
  if (dateRange.type !== DATE_RANGE_TYPE.ABSOLUTE && dateRange.type !== DATE_RANGE_TYPE.RELATIVE)
    return undefined;

  const range = getAbsoluteDateRange(dateRange);

  return {
    startDate: moment.unix(range.fromTimestamp),
    endDate: moment.unix(range.toTimestamp),
  };
}

function isRangeWithinUnavailableDates(startDate: Moment, endDate: Moment): boolean {
  return (
    startDate.isBetween(unavailableStartDate, unavailableEndDate, undefined, '[]') &&
    endDate.isBetween(unavailableStartDate, unavailableEndDate, undefined, '[]')
  );
}

function isRangeOverlappingWithUnavailableDates(startDate: Moment, endDate: Moment): boolean {
  const startDateOverlaps =
    startDate.isSameOrAfter(unavailableStartDate) && startDate.isSameOrBefore(unavailableEndDate);
  const endDateOverlaps =
    endDate.isSameOrAfter(unavailableStartDate) && endDate.isSameOrBefore(unavailableEndDate);

  return startDateOverlaps || endDateOverlaps;
}

export function useMoveDataUnavailable(dateRange: DateRange, heatType: HeatmapDataType): void {
  const hasFiredAppcuesFlowRef = useRef(false);

  const startAndEndDates = getStartAndEndDates(dateRange);
  const startDate = startAndEndDates?.startDate;
  const endDate = startAndEndDates?.endDate;
  const rangeWithin = startDate && endDate && isRangeWithinUnavailableDates(startDate, endDate);
  const rangeOverlaps =
    startDate &&
    endDate &&
    !rangeWithin &&
    isRangeOverlappingWithUnavailableDates(startDate, endDate);
  const shouldFireAppcuesFlow =
    heatType === HEATMAP_DATA_TYPES.MOVEMENT &&
    (rangeOverlaps || rangeWithin) &&
    !hasFiredAppcuesFlowRef.current;

  useEffect(() => {
    if (shouldFireAppcuesFlow) {
      const flowId = rangeWithin ? WITHIN_RANGE_FLOW_ID : OVERLAPS_RANGE_FLOW_ID;
      showAppcuesContent(flowId);
      hasFiredAppcuesFlowRef.current = true;
    }

    hasFiredAppcuesFlowRef.current = false;
  }, [shouldFireAppcuesFlow, rangeOverlaps, rangeWithin]);
}
